import type { ApiResponse } from "@entrilia/shared/api/types";
import type { FileDownloadInfo } from "@entrilia/shared/api/fileTypes";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import PdfPreview from "@entrilia/shared/components/previewFile/pdfViewer/PdfPreview";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import { isImageFile, isPdfFile, isVideoFile } from "@entrilia/shared/utilities/fileHelper";
import ErrorDocumentPreview from "./ErrorDocumentPreview";
import ImagePreview from "./ImagePreview";
import NotPossibleDocumentPreview from "./NotPossibleDocumentPreview";
import type { PdfHighlight } from "./PdfHighlightsPreview";
import PdfHighlightsPreview from "./PdfHighlightsPreview";
import VideoPreview from "./VideoPreview";

interface Props {
  getFileDownloadInfoCallback: () => Promise<ApiResponse<FileDownloadInfo>>;
  highlights?: PdfHighlight[];
  selectedCitationId?: string;
}

const DocumentPreview = ({ getFileDownloadInfoCallback, highlights, selectedCitationId }: Props) => {
  const [fileInfo, error, { isFetching }] = useFetch(getFileDownloadInfoCallback);
  if (error) {
    logError(error, "[DocumentPreview] getFileDownloadInfoCallback");
    return <ErrorDocumentPreview message={error.message} />;
  }

  if (isFetching || fileInfo === undefined) {
    return <InlineLoader />;
  }

  if (isPdfFile(fileInfo.fileName)) {
    return highlights?.length ? (
      <PdfHighlightsPreview
        fileSrc={fileInfo.downloadUrl}
        fileName={fileInfo.fileName}
        highlights={highlights}
        selectedCitationId={selectedCitationId}
        autoScrollToCitationId={selectedCitationId}
      />
    ) : (
      <PdfPreview fileSrc={fileInfo.downloadUrl} fileName={fileInfo.fileName} />
    );
  }

  if (isImageFile(fileInfo.fileName)) {
    return (
      <ImagePreview
        fileInfo={fileInfo}
        getFileDownloadInfoCallback={getFileDownloadInfoCallback}
        highlights={highlights}
        selectedCitationId={selectedCitationId}
      />
    );
  }

  if (isVideoFile(fileInfo.fileName)) {
    return <VideoPreview fileInfo={fileInfo} getFileDownloadInfoCallback={getFileDownloadInfoCallback} />;
  }

  return <NotPossibleDocumentPreview getFileDownloadInfoCallback={getFileDownloadInfoCallback} />;
};

export default DocumentPreview;
